// The ServiceTitan move (gap #16; the st-migration prototype): one office place for getting the
// company's own data out of ServiceTitan. A side list of the parts, as Books has: the overview (the
// run, the owner's written authorisation and the cutover checklist), mappings, photos, the review
// queue and the reconciliation with its sign-offs. It runs against the fake ServiceTitan until the
// owner's written OK for a real pull; the server's refusals show in its words.

import { useState } from 'react';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Loading, Panel, errorText, useQuery, useToast } from '../books/ui.tsx';
import { client } from '../api.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import { Mappings } from './Mappings.tsx';
import { TrialPanel } from './Trial.tsx';
import {
  authorisationText,
  holds,
  loadAskedWords,
  loadInput,
  mayRecord,
  MIGRATION_CHANGED,
  groupsTile,
  mirrorPanel,
  type MirrorHealth,
  OPERATE_PERMS,
  OWNER_PERMS,
  RUN_STATE,
  WHO,
  type PartProps,
  type Run,
  type ParkedUnit,
  type SignOffYear,
  signOffLines,
  type Status,
  type Step,
  unitLabel,
  yearToLoad,
} from './model.ts';
import { Photos } from './Photos.tsx';
import { Reconcile } from './Reconcile.tsx';
import { ReviewQueue } from './ReviewQueue.tsx';
import { RuleSections } from '../settings/RuleSection.tsx';
import { settingsRights } from '../settings/settings-model.ts';
import '../books/books.css';
import '../shared/office-lists.css';
import '../review/review.css';
import './migration.css';
import '@flowology/ui-web/base.css';
import '../blueprint/setup.css';
import '../blueprint/setup-migration.css';

type Part = 'overview' | 'mappings' | 'photos' | 'review' | 'reconcile';

const PARTS: { part: Part; label: string; lede: string }[] = [
  {
    part: 'overview',
    label: 'Overview',
    lede: 'The run, the owner’s written OK, and every step to switching ServiceTitan off.',
  },
  {
    part: 'mappings',
    label: 'Mappings',
    lede: 'What each ServiceTitan value becomes here. The owner approves each group.',
  },
  { part: 'photos', label: 'Photos', lede: 'Files the pipeline couldn’t tie to a job with confidence.' },
  {
    part: 'review',
    label: 'Review queue',
    lede: 'Records that didn’t parse or load, for a person to decide.',
  },
  {
    part: 'reconcile',
    label: 'Reconciliation',
    lede: 'Each year checked against ServiceTitan, and the owner’s sign-off.',
  },
];

export function MigrationScreen({ nav, held }: { nav: NavProps; held: readonly string[] }) {
  const bp = useDesign() === 'blueprint';
  const [part, setPart] = useState<Part>('overview');
  const [toast, setToast] = useToast();
  const here = PARTS.find((p) => p.part === part)!;
  const company = nav.companyName ?? 'the company';
  const props: PartProps = {
    held,
    toast: setToast,
    timeZone: nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  return (
    <div className={bp ? 'app books migration bp-setup flw' : 'app books migration'}>
      <Header
        nav={nav}
        title={part === 'overview' ? `Getting ${company}’s data out of ServiceTitan` : here.label}
        lede={here.lede}
      />
      <div className="books-body">
        <nav className={bp ? 'books-parts bp-parts' : 'books-parts'} aria-label="ServiceTitan move">
          {PARTS.map((p) => (
            <button
              key={p.part}
              className={bp ? undefined : `btn quiet${part === p.part ? ' on' : ''}`}
              aria-current={part === p.part ? 'page' : undefined}
              onClick={() => setPart(p.part)}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {part === 'overview' && <Overview {...props} company={company} go={setPart} />}
            {/* Blueprint: how often the mirror copies, under the run it paces (the setup audit) */}
            {part === 'overview' && bp && <RuleSections page="migration" rights={settingsRights(held)} />}
            {part === 'mappings' && <Mappings {...props} />}
            {part === 'photos' && <Photos {...props} />}
            {part === 'review' && <ReviewQueue {...props} />}
            {part === 'reconcile' && <Reconcile {...props} />}
          </div>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

const when = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

function Overview({
  held,
  toast,
  timeZone,
  company,
  go,
}: PartProps & { company: string; go: (p: Part) => void }) {
  const status = useQuery<Status>('migration.status', {});
  const list = useQuery<{ steps: Step[]; next: string | null }>('migration.cutoverChecklist', {});
  const reload = async () => {
    await Promise.all([status.reload(), list.reload()]);
  };
  const [recording, setRecording] = useState<Step | null>(null);
  const [authorising, setAuthorising] = useState(false);
  const bp = useDesign() === 'blueprint';
  if (!status.data || !list.data)
    return (
      <Panel title="Where it stands">
        <Loading error={status.error ?? list.error} what="the migration" retry={() => void reload()} />
      </Panel>
    );
  const s = status.data;
  const authorised = list.data.steps.find((x) => x.key === 'authorised')?.done ?? false;
  const totalUnits = s.units.pending + s.units.running + s.units.done + s.units.parked;
  // where it stands, the run and the steps; in Blueprint the steps (the work) on the left and the run
  // (what it needs) on the right, under where it stands
  const stands = (
    <Panel title="Where it stands">
      <div className="mig-tiles">
        <Tile big={`${s.units.done} of ${totalUnits}`} label="extraction units done" />
        <Tile big={s.rows.toLocaleString('en-CA')} label="records pulled" />
        <Tile
          big={`${s.files.downloaded.toLocaleString('en-CA')}`}
          label={`files downloaded${s.files.failed ? `, ${s.files.failed} failed` : ''}${s.files.deferred ? `, ${s.files.deferred} too large for now` : ''}`}
        />
        {s.photos ? (
          <Tile
            big={`${(s.photos.listed + s.photos.failed).toLocaleString('en-CA')} of ${(s.photos.pending + s.photos.listed + s.photos.failed).toLocaleString('en-CA')}`}
            label="jobs’ photo lists read"
          />
        ) : null}
        <Tile big={String(s.linksWaiting)} label="photos waiting for a person" onClick={() => go('photos')} />
        <Tile
          big={String(s.reviewOpenServiceTitan ?? s.reviewOpen)}
          label={
            s.reviewOpenServiceTitan === undefined ? 'records to review' : 'ServiceTitan records to review'
          }
          onClick={() => go('review')}
        />
        {s.reviewOpenBrook ? (
          <Tile
            big={s.reviewOpenBrook.toLocaleString('en-CA')}
            label="rows of Brook’s import to review"
            onClick={() => go('review')}
          />
        ) : null}
        <Tile {...groupsTile(s)} onClick={() => go('mappings')} />
        <Tile
          big={s.yearsSigned.length ? s.yearsSigned.join(', ') : 'None'}
          label="years signed off"
          onClick={() => go('reconcile')}
        />
      </div>
      {s.signOff && s.signOff.length > 0 && <LeftForOk years={s.signOff} go={go} />}
      {s.units.parked > 0 && (
        <div className="notice warn mig-parked">
          <p>
            {s.units.parked} {s.units.parked === 1 ? 'unit is' : 'units are'} parked after repeated failures.
            The extract isn’t complete until they’re retried and done.
          </p>
          {s.parkedUnits && s.parkedUnits.length > 0 && (
            <ul>
              {s.parkedUnits.map((u) => (
                <Parked
                  key={`${u.entity}:${u.year}`}
                  unit={u}
                  may={holds(held, OPERATE_PERMS)}
                  timeZone={timeZone}
                  onRetried={async () => {
                    toast(`${unitLabel(u)} is back in the queue, from the page it reached.`);
                    await reload();
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      )}
      {s.photos && s.photos.failed > 0 && (
        <PhotosRefused
          photos={s.photos}
          may={holds(held, OPERATE_PERMS)}
          onRetried={async (n) => {
            toast(`${n} ${n === 1 ? 'job’s photo list is' : 'jobs’ photo lists are'} back in the queue.`);
            await reload();
          }}
        />
      )}
    </Panel>
  );
  const runPanel = (
    <RunPanel
      run={s.run}
      toDo={s.toDo ?? []}
      held={held}
      authorised={authorised}
      timeZone={timeZone}
      toast={toast}
      reload={reload}
    />
  );
  const steps = (
    <Panel title="Every step to switching ServiceTitan off">
      {!authorised && (
        <div className="notice warn mig-auth">
          <p>
            <b>Nothing runs before the owner’s written OK.</b> The owner authorises the extraction of{' '}
            {company}’s own data by typing their full name.
          </p>
          {holds(held, OWNER_PERMS) && (
            <button type="button" className="btn primary" onClick={() => setAuthorising(true)}>
              Authorise the extraction
            </button>
          )}
        </div>
      )}
      <ol className="mig-steps">
        {list.data.steps.map((st) => (
          <li key={st.key} className={st.done ? 'done' : list.data!.next === st.key ? 'next' : undefined}>
            <span className="mig-mark" aria-hidden="true">
              {st.done ? '✓' : '○'}
            </span>
            <div>
              <b>{st.label}</b>
              <span className="muted small block">
                {st.done ? 'Done' : (WHO[st.by] ?? st.by)}
                {st.doneAt ? ` · ${when(st.doneAt, timeZone)}` : ''}
                {st.detail ? ` · ${st.detail}` : ''}
              </span>
              {st.evidence && <span className="small block">“{st.evidence}”</span>}
            </div>
            {mayRecord(st, held) && (
              <button type="button" className="btn" onClick={() => setRecording(st)}>
                Record it
              </button>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  );
  return (
    <>
      {stands}
      {s.mirrorHealth && <MirrorPanel health={s.mirrorHealth} timeZone={timeZone} />}
      {/* once authorised: a trial on what's loaded, until the company mirrors or cuts over */}
      {authorised && !s.mirrorHealth && s.run?.phase !== 'live' && (
        <TrialPanel held={held} company={company} timeZone={timeZone} toast={toast} />
      )}
      {bp ? (
        <div className="bp-grid2">
          <div>{steps}</div>
          <div>{runPanel}</div>
        </div>
      ) : (
        <>
          {runPanel}
          {steps}
        </>
      )}
      {recording && (
        <RecordStep
          step={recording}
          onClose={() => setRecording(null)}
          onDone={() => {
            toast(`Recorded: ${recording.label}.`);
            setRecording(null);
            void reload();
          }}
        />
      )}
      {authorising && (
        <Authorise
          company={company}
          onClose={() => setAuthorising(false)}
          onDone={() => {
            toast('Authorised. The extraction can start.');
            setAuthorising(false);
            void reload();
          }}
        />
      )}
    </>
  );
}

/** One parked unit: what failed, how often, since when, and Retry (migration.retryUnit). */
function Parked({
  unit: u,
  may,
  timeZone,
  onRetried,
}: {
  unit: ParkedUnit;
  may: boolean;
  timeZone: string;
  onRetried: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.retryUnit', { entity: u.entity, year: u.year });
      await onRetried();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="mig-parked-unit">
      <span>
        <b>{unitLabel(u)}</b>
        <span className="small block">
          {u.attempts} {u.attempts === 1 ? 'try' : 'tries'} · parked {when(u.since, timeZone)}
          {u.lastError ? ` · ${u.lastError}` : ''}
        </span>
      </span>
      {may && (
        <SaveButton
          busy={busy}
          busyLabel="Retrying…"
          error={error}
          className="btn"
          onClick={() => void retry()}
        >
          Retry
        </SaveButton>
      )}
    </li>
  );
}

/** Jobs' photo lists ServiceTitan refused (M9): said with the latest refusal, retried once it's set right. */
function PhotosRefused({
  photos,
  may,
  onRetried,
}: {
  photos: NonNullable<Status['photos']>;
  may: boolean;
  onRetried: (n: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ retried: number }>('migration.retryPhotos', {});
      await onRetried(r.retried);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="notice warn mig-parked">
      <p>
        {photos.failed} {photos.failed === 1 ? 'job’s photos' : 'jobs’ photos'} couldn’t be listed from
        ServiceTitan. The rest of the data goes on without them.
        {photos.lastError ? ` The latest answer: ${photos.lastError}` : ''}
      </p>
      {may && (
        <SaveButton
          busy={busy}
          busyLabel="Retrying…"
          error={error}
          className="btn"
          onClick={() => void retry()}
        >
          Retry the photo lists
        </SaveButton>
      )}
    </div>
  );
}

/** Beside the records to review: what each year's sign-off still waits on, in words (the lead, 24 Oct). */
function LeftForOk({ years, go }: { years: SignOffYear[]; go: (p: Part) => void }) {
  return (
    <div className="mig-ok">
      <b>What’s left for your OK</b>
      <ul>
        {signOffLines(years).map((l) => (
          <li key={l.year} className={l.ready ? 'ready' : undefined}>
            {l.text}
          </li>
        ))}
      </ul>
      <button type="button" className="linkish mig-link" onClick={() => go('reconcile')}>
        Reconciliation
      </button>
    </div>
  );
}

/** Lists shown before "Show every list". */
const MIRROR_ROWS = 8;

/**
 * While mirroring ServiceTitan (st-mirror spec §1, BL-090): the last pass, how far behind the copy is,
 * what's unfinished and why, ServiceTitan's back-off, and each list, furthest behind first.
 */
function MirrorPanel({ health, timeZone }: { health: MirrorHealth; timeZone: string }) {
  const [all, setAll] = useState(false);
  const v = mirrorPanel(health, (iso) => when(iso, timeZone));
  const rows = all ? v.rows : v.rows.slice(0, MIRROR_ROWS);
  return (
    <Panel title="Copying from ServiceTitan">
      <div className="mig-tiles">
        <Tile big={v.lag} label="the furthest a list is behind" />
        <Tile big={v.lastPass} label="the last pass" />
        <Tile
          big={String(health.incomplete.length)}
          label={health.incomplete.length ? `unfinished: ${v.unfinished}` : 'lists unfinished'}
        />
        <Tile big={String(health.photoListsWaiting)} label="photo lists waiting" />
      </div>
      {v.backoff && <p className="notice mig-mirror-note">{v.backoff}</p>}
      {v.alert && <p className="notice warn mig-mirror-note">{v.alert}</p>}
      {rows.length > 0 && (
        <ul className="mig-mirror" aria-label="Each list Flowology copies">
          {rows.map((r) => (
            <li key={r.entity} className={r.warn ? 'warn' : undefined}>
              <b>{r.label}</b>
              <span className="mig-mirror-behind">{r.behind}</span>
              {r.detail && <span className="muted small block">{r.detail}</span>}
            </li>
          ))}
        </ul>
      )}
      {v.rows.length > MIRROR_ROWS && (
        <button type="button" className="btn quiet" onClick={() => setAll(!all)}>
          {all ? 'Show the furthest behind' : `Show every list (${v.rows.length})`}
        </button>
      )}
    </Panel>
  );
}

function Tile({ big, label, onClick }: { big: string; label: string; onClick?: () => void }) {
  const body = (
    <>
      <b className="big">{big}</b>
      <span className="tile-label">{label}</span>
    </>
  );
  return onClick ? (
    <button type="button" className="tile mig-tile" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="tile mig-tile">{body}</div>
  );
}

function RunPanel({
  run,
  toDo,
  held,
  authorised,
  timeZone,
  toast,
  reload,
}: {
  run: Run | null;
  /** What has to be done before a load runs (the load refuses until then). */
  toDo: readonly string[];
  held: readonly string[];
  authorised: boolean;
  timeZone: string;
  toast: (m: string) => void;
  reload: () => Promise<void>;
}) {
  const [tenant, setTenant] = useState(run?.stTenant ?? '');
  // the year a load takes: picked, else the newest not loaded yet (#80: never every staged year)
  const [picked, setPicked] = useState<number | null>(null);
  const loadYear = picked ?? yearToLoad(run);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ on: string; text: string } | null>(null);
  const may = holds(held, OPERATE_PERMS);
  const state = run?.state ?? 'off';
  /** `said` may read the action's answer (the load's, while mirroring, says when it runs). */
  const act = async (
    on: string,
    action: string,
    input: unknown,
    said: string | ((out: unknown) => string),
  ) => {
    setBusy(on);
    setError(null);
    try {
      const out = await client.call(action, input);
      toast(typeof said === 'string' ? said : said(out));
      window.dispatchEvent(new Event(MIGRATION_CHANGED));
      await reload();
    } catch (e) {
      setError({ on, text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };
  const errOn = (on: string) => (error?.on === on ? error.text : null);
  const bp = useDesign() === 'blueprint';
  // The run's controls, each once. Riverstone lays them in one row; Blueprint puts the one action
  // at rest first (start or resume while it's stopped, load while it runs) and the rest under it.
  const startIt = (
    <>
      {/* set before the first start; fixable while it isn't running (a typo, the wrong tenant) */}
      <label className="inline-label">
        <span className="flabel">ServiceTitan tenant id</span>
        <input className="field" maxLength={100} value={tenant} onChange={(e) => setTenant(e.target.value)} />
      </label>
      <SaveButton
        busy={busy === 'start'}
        busyLabel="Starting…"
        error={errOn('start')}
        disabled={!authorised || !tenant.trim()}
        onClick={() =>
          void act(
            'start',
            'migration.setRun',
            {
              state: 'running',
              ...(tenant.trim() !== run?.stTenant ? { stTenant: tenant.trim() } : {}),
            },
            state === 'paused' ? 'Running again, from where it stopped.' : 'Started.',
          )
        }
      >
        {state === 'paused' ? 'Resume' : 'Start the extraction'}
      </SaveButton>
    </>
  );
  const pauseIt = (
    <SaveButton
      busy={busy === 'pause'}
      busyLabel="Pausing…"
      error={errOn('pause')}
      className="btn"
      onClick={() => void act('pause', 'migration.setRun', { state: 'paused' }, 'Paused.')}
    >
      Pause
    </SaveButton>
  );
  const turnOff = (
    <SaveButton
      busy={busy === 'off'}
      busyLabel="Turning off…"
      error={errOn('off')}
      className="btn"
      onClick={() => void act('off', 'migration.setRun', { state: 'off' }, 'Turned off.')}
    >
      Turn it off
    </SaveButton>
  );
  const toDoNote = (
    <div className="notice warn" role="status">
      <p>To do before loading:</p>
      <ul>
        {toDo.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </div>
  );
  const yearPick = loadYear !== null && run && run.years.length > 1 && (
    <label className="inline-label">
      <span className="flabel">Year to load</span>
      <select className="field" value={loadYear} onChange={(e) => setPicked(Number(e.target.value))}>
        {[...run.years]
          .sort((a, b) => b - a)
          .map((y) => (
            <option key={y} value={y}>
              {y}
              {(run.loadedYears ?? []).includes(y) ? ' (loaded)' : ''}
            </option>
          ))}
      </select>
    </label>
  );
  const loadIt = (
    <SaveButton
      className={bp && state === 'running' ? 'btn primary' : 'btn'}
      busy={busy === 'load'}
      busyLabel="Asking…"
      error={errOn('load')}
      onClick={() =>
        void act('load', 'migration.requestLoad', loadInput(run, picked), (out) =>
          loadAskedWords(out as Run, (iso) => when(iso, timeZone)),
        )
      }
    >
      {run?.phase === 'mirror'
        ? 'Load ServiceTitan’s changes now'
        : loadYear !== null
          ? `Load ${loadYear}`
          : 'Load what’s staged'}
    </SaveButton>
  );
  return (
    <Panel
      title="The run"
      aside={<span className={`chip say${state === 'running' ? ' ok' : ''}`}>{RUN_STATE[state]}</span>}
    >
      <dl className="kv">
        <dt>Where it is</dt>
        <dd>{run ? run.phase.replace(/_/g, ' ') : 'Not started'}</dd>
        {run?.stTenant && (
          <>
            <dt>ServiceTitan tenant</dt>
            <dd>{run.stTenant}</dd>
          </>
        )}
        {run?.years.length ? (
          <>
            <dt>Years</dt>
            <dd>{[...run.years].sort((a, b) => b - a).join(', ')}</dd>
          </>
        ) : null}
        {run?.lastStep && (
          <>
            <dt>Last did</dt>
            <dd>
              {run.lastStep}
              {run.lastTickAt ? (
                <span className="muted small"> · {when(run.lastTickAt, timeZone)}</span>
              ) : null}
            </dd>
          </>
        )}
        {run?.loadRequested && (
          <>
            <dt>Load</dt>
            <dd>{loadAskedWords(run, (iso) => when(iso, timeZone))}</dd>
          </>
        )}
      </dl>
      {run?.lastError && (
        <p className="notice warn">
          Last error: {run.lastError}
          {/* books: a failed reconciliation is retried by asking for the load again, with or without anything new */}
          {run.lastError.startsWith('reconciliation failed') &&
            ' “Load what’s staged” reconciles again; until then a year can’t be signed off.'}
        </p>
      )}
      {may ? (
        bp ? (
          <>
            <div className="row-actions mig-run">
              {state === 'running' ? (
                <>
                  {yearPick}
                  {loadIt}
                </>
              ) : (
                startIt
              )}
            </div>
            <div className="mig-run-more" role="group" aria-label="The run’s other controls">
              {state === 'running' && pauseIt}
              {state !== 'running' && (
                <>
                  {yearPick}
                  {loadIt}
                </>
              )}
              {state !== 'off' && turnOff}
            </div>
            {toDo.length > 0 && toDoNote}
          </>
        ) : (
          <div className="row-actions mig-run">
            {state !== 'running' && startIt}
            {state === 'running' && pauseIt}
            {state !== 'off' && turnOff}
            {toDo.length > 0 && toDoNote}
            {yearPick}
            {loadIt}
          </div>
        )
      ) : (
        <p className="muted small">The owner or a manager runs it.</p>
      )}
      {may && !authorised && <p className="muted small">Starting waits for the owner’s written OK below.</p>}
    </Panel>
  );
}

function RecordStep({ step, onClose, onDone }: { step: Step; onClose: () => void; onDone: () => void }) {
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const record = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.setPrecondition', { key: step.key, evidence: evidence.trim() });
      onDone();
    } catch (e) {
      // e.g. "Not yet. First: …" when a step comes out of order.
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Record a step"
      outcome={step.label}
      confirm="Record it"
      ready={!!evidence.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void record()}
      onClose={onClose}
    >
      <label>
        The evidence (required)
        <textarea
          className="field"
          rows={3}
          maxLength={2000}
          value={evidence}
          placeholder="What shows it’s done: a ticket, an email, who confirmed it"
          onChange={(e) => setEvidence(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function Authorise({
  company,
  onClose,
  onDone,
}: {
  company: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statement = authorisationText(company);
  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('migration.authorise', { typedName: name.trim(), statement });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title="Authorise the extraction"
      outcome={<>The owner’s written OK, kept as evidence on the cutover checklist.</>}
      confirm="Sign and authorise"
      ready={name.trim().length > 1}
      busy={busy}
      error={error}
      onConfirm={() => void sign()}
      onClose={onClose}
    >
      <ol className="mig-letter">
        {statement.split('\n').map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ol>
      <label>
        Type your full name to sign
        <input className="field" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
    </MoneySheet>
  );
}
